/**
 * EventDateBadge component for displaying event dates
 * Shared component used in both EventCard and EventDetail
 */

interface EventDateBadgeProps {
  date: Date;
  size?: 'default' | 'compact' | 'large';
  className?: string;
  isPast?: boolean;
}

/**
 * Date badge component with year
 */
export function EventDateBadge({
  date,
  size = 'default',
  className = '',
  isPast = false,
}: EventDateBadgeProps) {
  const month = date.toLocaleDateString('en-GB', { month: 'short' });
  const day = date.getDate();
  const year = date.getFullYear();

  const sizeConfig = {
    default: {
      container: 'h-[72px] w-16',
      month: 'text-xs',
      day: 'text-2xl',
      year: 'text-[10px]',
    },
    compact: {
      container: 'h-[72px] w-16',
      month: 'text-xs',
      day: 'text-2xl',
      year: 'text-[10px]',
    },
    large: {
      container: 'h-24 w-20',
      month: 'text-xs',
      day: 'text-3xl',
      year: 'text-[10px]',
    },
  };

  const config = sizeConfig[size];

  return (
    <div
      className={`flex flex-col items-center justify-center rounded-lg bg-primary/10 text-center ${config.container} ${isPast ? 'opacity-50' : ''} ${className}`}
    >
      <div className={`font-medium text-muted-foreground ${config.month}`} suppressHydrationWarning>
        {month}
      </div>
      <div className={`font-bold text-primary ${config.day}`} suppressHydrationWarning>
        {day}
      </div>
      <div className={`font-medium text-muted-foreground ${config.year}`} suppressHydrationWarning>
        {year}
      </div>
    </div>
  );
}

interface EventDateBadgeOverlayProps {
  date: Date;
  className?: string;
  isPast?: boolean;
}

/**
 * Date badge overlay for images
 */
export function EventDateBadgeOverlay({
  date,
  className = '',
  isPast = false,
}: EventDateBadgeOverlayProps) {
  const month = date.toLocaleDateString('en-GB', { month: 'short' });
  const day = date.getDate();
  const year = date.getFullYear();

  return (
    <div
      className={`absolute left-2 top-2 flex h-[64px] w-14 flex-col items-center justify-center rounded bg-background/90 backdrop-blur-sm text-center shadow-md sm:h-[72px] sm:w-16 ${isPast ? 'opacity-50' : ''} ${className}`}
    >
      <div className="text-xs font-medium text-muted-foreground" suppressHydrationWarning>
        {month}
      </div>
      <div className="text-2xl font-bold text-primary" suppressHydrationWarning>
        {day}
      </div>
      <div className="text-[10px] font-medium text-muted-foreground" suppressHydrationWarning>
        {year}
      </div>
    </div>
  );
}
